import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { providerSettlementsApi, actionMessage } from '../../services/providerSettlementsApi';
import {
  ProviderSettlement,
  ProviderSettlementDetail as Detail,
  formatPeriod,
  formatTripDate,
  invoiceableAmount,
  legLabel,
  dubaiToday,
  monthLabel,
  settlementVat,
  tripsWithoutCost,
  vatIncluded,
} from '../../types/providerSettlements';
import Money, { formatAed } from '../finance/Money';
import InfoTip from '../finance/InfoTip';

/** DRAFT / PAID / VOID → the admin badge palette. */
export function ProviderSettlementStatusBadge({ status }: { status: string }) {
  const s = String(status).toUpperCase();
  const cls = s === 'PAID' ? 'approved' : s === 'VOID' ? 'rejected' : 'pending';
  return <span className={`admin-badge ${cls}`} data-testid="provider-settlement-status">{s}</span>;
}

/**
 * The settlement total. Blank is a first-class value (AC-43): when any trip has no configured cost
 * the backend sends `amount: null` together with `costConfigured: false` and its own `amountNote`
 * ("N trips, cost not configured"), and it renders as an em dash with that note beside it. Showing
 * 0.00 would read as "the trips were free", which is the opposite of the truth.
 *
 * Once paid, `paymentAmount` is the figure that matters — what actually left the bank — so it wins
 * over the derived total.
 */
export function SettlementAmount({ settlement, hideTip }: { settlement: ProviderSettlement; hideTip?: boolean }) {
  if (settlement.paymentAmount != null) {
    return <Money amount={settlement.paymentAmount} glossaryKey="providerSettlementAmount" hideTip={hideTip} />;
  }
  if (settlement.amount === null || settlement.amount === undefined) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
        <Money amount={null} glossaryKey="providerSettlementAmount" hideTip={hideTip} />
        <span style={{ fontSize: '0.6875rem', fontWeight: 500, color: 'var(--admin-text-muted)' }} data-testid="amount-not-configured">
          {settlement.amountNote ?? `${settlement.tripCount} trip${settlement.tripCount === 1 ? '' : 's'}, cost not configured`}
        </span>
      </span>
    );
  }
  return <Money amount={settlement.amount} glossaryKey="providerSettlementAmount" hideTip={hideTip} />;
}

type Panel = null | 'void' | 'pay';

interface ProviderSettlementDetailProps {
  /** The clicked list row; `null` keeps the drawer closed. */
  settlement: ProviderSettlement | null;
  onClose: () => void;
  /** Called after any change (exclude / void / pay) so the list behind refetches. */
  onChanged: () => void;
}

/**
 * Spec 053 Flow F7 steps 2–3 — the provider settlement drawer.
 *
 * Shows the trips the settlement collected (order number, leg, date, driver, cost — AC-42) and the
 * three things an admin does with a DRAFT: exclude a trip back into the pool, void the whole
 * settlement (all trips return, AC-42), or record the payment (reference + amount + proof, BR-29).
 *
 * Every rejection is rendered inline beside the control that caused it rather than as a toast: the
 * 409 "Trip already settled" (AC-44) belongs on the trip row's action, and the 400
 * "Enter the invoiced amount" (AC-43) belongs in the payment form where the field is.
 */
export default function ProviderSettlementDetail({ settlement, onClose, onChanged }: ProviderSettlementDetailProps) {
  const auth = useAuth();
  const id = settlement?.id ?? null;

  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [panel, setPanel] = useState<Panel>(null);
  const [busy, setBusy] = useState(false);
  const [tripError, setTripError] = useState<string | null>(null);
  const [voidError, setVoidError] = useState<string | null>(null);
  const [payError, setPayError] = useState<string | null>(null);

  const [voidReason, setVoidReason] = useState('');
  const [reference, setReference] = useState('');
  const [amount, setAmount] = useState('');
  const [proof, setProof] = useState<File | null>(null);
  const [payVatRegistered, setPayVatRegistered] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id || !auth.token) return;
    setLoading(true);
    setError(null);
    try {
      setDetail(await providerSettlementsApi.get(id, auth.token));
    } catch (err) {
      setDetail(null);
      setError(actionMessage(err, 'Failed to load the provider settlement'));
    } finally {
      setLoading(false);
    }
  }, [id, auth.token]);

  useEffect(() => {
    if (!id) return;
    // A freshly-opened drawer must not show the previous settlement's state.
    setDetail(null);
    setPanel(null);
    setTripError(null);
    setVoidError(null);
    setPayError(null);
    setVoidReason('');
    setReference('');
    setProof(null);
    setInvoiceFile(null);
    setInvoiceError(null);
    load();
  }, [id, load]);

  // Pre-fill only when every trip has a cost (BR-29); otherwise it must be typed. #577 — the figure
  // offered is what the provider will INVOICE (cost + VAT when they are registered), not the ex-VAT
  // total, which could never clear the payable the ledger accrued.
  const offeredAmount = detail ? invoiceableAmount(detail.settlement) : null;
  useEffect(() => {
    setAmount(offeredAmount != null ? offeredAmount.toFixed(2) : '');
  }, [offeredAmount]);
  // The VAT status the payment claims on — pre-ticked from the trips' snapshot, confirmed by the admin.
  const snapshotVat = detail?.settlement.providerVatRegistered === true;
  useEffect(() => { setPayVatRegistered(snapshotVat); }, [snapshotVat]);

  useEffect(() => {
    if (!id) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [id, onClose]);

  if (!settlement) return null;

  // The clicked row renders the header immediately; the fetched detail supersedes it once it lands.
  const view = detail?.settlement ?? settlement;
  const isDraft = String(view.status).toUpperCase() === 'DRAFT';
  const noCostTrips = detail ? tripsWithoutCost(detail.trips) : 0;
  // #577 — null for a provider with no TRN (no tax invoice, so no VAT to recover) or when the
  // amount itself is unknown. Rendering 0.00 would read as "the VAT is zero" rather than "there is
  // no VAT on this settlement at all", which is the same trap AC-43's blank amount exists to avoid.
  const vat = settlementVat(view);

  const excludeTrip = async (tripId: string) => {
    if (!id || !auth.token || busy) return;
    setBusy(true);
    setTripError(null);
    try {
      await providerSettlementsApi.excludeTrip(id, tripId, auth.token);
      await load();
      onChanged();
    } catch (err) {
      setTripError(actionMessage(err, 'Failed to exclude the trip'));
    } finally {
      setBusy(false);
    }
  };

  const voidSettlement = async (e: FormEvent) => {
    e.preventDefault();
    if (!id || !auth.token || busy) return;
    if (!voidReason.trim()) {
      setVoidError('Enter a reason for voiding this settlement');
      return;
    }
    setBusy(true);
    setVoidError(null);
    try {
      await providerSettlementsApi.void(id, voidReason.trim(), auth.token);
      setPanel(null);
      await load();
      onChanged();
    } catch (err) {
      setVoidError(actionMessage(err, 'Failed to void the settlement'));
    } finally {
      setBusy(false);
    }
  };

  const recordPayment = async (e: FormEvent) => {
    e.preventDefault();
    if (!id || !auth.token || busy) return;

    // Only the amount is mandatory since 2026-09-27; the reference and the invoice are optional.
    const parsed = Number(amount);
    if (!amount.trim() || !Number.isFinite(parsed) || parsed <= 0) { setPayError('Enter the invoiced amount'); return; }

    setBusy(true);
    setPayError(null);
    try {
      await providerSettlementsApi.pay(id, {
        amount: parsed,
        reference: reference.trim() || undefined,
        invoice: proof,
        providerVatRegistered: payVatRegistered,
      }, auth.token);
      setPanel(null);
      await load();
      onChanged();
    } catch (err) {
      setPayError(actionMessage(err, 'Failed to record the payment'));
    } finally {
      setBusy(false);
    }
  };

  const uploadInvoice = async (e: FormEvent) => {
    e.preventDefault();
    if (!id || !auth.token || busy) return;
    if (!invoiceFile) { setInvoiceError('Choose the invoice file first'); return; }
    setBusy(true);
    setInvoiceError(null);
    try {
      setDetail(await providerSettlementsApi.attachInvoice(id, invoiceFile, auth.token));
      setInvoiceFile(null);
      onChanged();
    } catch (err) {
      setInvoiceError(actionMessage(err, 'Failed to upload the invoice'));
    } finally {
      setBusy(false);
    }
  };

  const isPaid = String(view.status).toUpperCase() === 'PAID';

  return (
    <div className="admin-modal-overlay" style={{ justifyContent: 'flex-end', alignItems: 'stretch' }} onClick={onClose}>
      <div
        className="admin-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Provider settlement for ${view.providerName}`}
        style={{
          width: '760px', maxWidth: '100vw', height: '100vh', margin: 0,
          borderRadius: '0.75rem 0 0 0.75rem', overflowY: 'auto', display: 'flex', flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal-header">
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            {view.providerName}
            <span
              style={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 500, color: 'var(--admin-text-muted)' }}
              data-testid="detail-settlement-number"
            >
              {view.settlementNumber}
            </span>
            <ProviderSettlementStatusBadge status={view.status} />
          </h3>
          <button type="button" className="admin-modal-close" aria-label="Close" onClick={onClose}>&times;</button>
        </div>

        <div className="admin-modal-body" style={{ flex: 1 }}>
          {error && <div className="admin-info-banner danger" role="alert" data-testid="detail-error">{error}</div>}
          {loading && <div className="admin-loading" data-testid="detail-loading"><div className="admin-loading-spinner"></div></div>}

          {detail && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.75rem' }} data-testid="detail-summary">
                <div style={{ padding: '0.75rem', background: 'var(--admin-bg)', borderRadius: '6px' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>Period</div>
                  <div style={{ fontSize: '1rem', fontWeight: 700, marginTop: '0.25rem' }}>
                    {formatPeriod(view.periodStart, view.periodEnd)}
                  </div>
                </div>
                <div style={{ padding: '0.75rem', background: 'var(--admin-bg)', borderRadius: '6px' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', display: 'flex', alignItems: 'center' }}>
                    Trips<InfoTip glossaryKey="providerSettlementTripCount" />
                  </div>
                  <div style={{ fontSize: '1rem', fontWeight: 700, marginTop: '0.25rem' }}>
                    <Money amount={view.tripCount} glossaryKey="providerSettlementTripCount" format="count" hideTip />
                  </div>
                </div>
                <div style={{ padding: '0.75rem', background: 'var(--admin-bg)', borderRadius: '6px' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', display: 'flex', alignItems: 'center' }}>
                    {view.paymentAmount != null ? 'Paid' : 'Amount'}<InfoTip glossaryKey="providerSettlementAmount" />
                  </div>
                  <div style={{ fontSize: '1rem', fontWeight: 700, marginTop: '0.25rem' }}>
                    <SettlementAmount settlement={view} hideTip />
                  </div>
                  {view.paymentAmount == null && vat != null && (
                    <div
                      style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--admin-text-muted)', marginTop: '0.25rem' }}
                      data-testid="summary-vat"
                    >
                      + <Money amount={vat} glossaryKey="providerSettlementVat" hideTip /> VAT
                    </div>
                  )}
                </div>
              </div>

              {view.paidAt && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: 'var(--admin-text-muted)' }} data-testid="paid-meta">
                  Paid {formatTripDate(view.paidAt)}{view.paymentReference ? ` · reference ${view.paymentReference}` : ''}
                  {view.vatAmount != null && (
                    <> · VAT claimed <Money amount={view.vatAmount} glossaryKey="providerSettlementVatClaimed" hideTip /></>
                  )}
                  {detail.invoiceUrl && (
                    <> · <a className="admin-link" href={detail.invoiceUrl} target="_blank" rel="noreferrer" data-testid="invoice-link">Invoice</a></>
                  )}
                  {detail.paymentProofUrl && (
                    <> · <a className="admin-link" href={detail.paymentProofUrl} target="_blank" rel="noreferrer">Payment proof</a></>
                  )}
                </div>
              )}
              {isPaid && !detail.invoiceUrl && (
                <form className="admin-info-banner info" style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}
                  onSubmit={uploadInvoice} data-testid="invoice-missing">
                  <span>Invoice missing — upload the provider’s invoice when you have it.</span>
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg" aria-label="Provider invoice"
                    onChange={(e) => setInvoiceFile(e.target.files?.[0] ?? null)} />
                  <button type="submit" className="admin-btn admin-btn-sm admin-btn-primary" disabled={busy}>
                    {busy ? 'Uploading…' : 'Upload invoice'}
                  </button>
                  {invoiceError && <span role="alert" data-testid="invoice-error" style={{ color: 'var(--admin-danger, #b91c1c)' }}>{invoiceError}</span>}
                </form>
              )}
              {view.voidReason && (
                <div className="admin-info-banner info" role="status" style={{ marginTop: '0.5rem' }} data-testid="void-meta">
                  <strong>Voided:</strong> {view.voidReason}
                </div>
              )}
              {/* Advice for recording a payment — meaningless once the settlement is paid or void. */}
              {isDraft && noCostTrips > 0 && (
                <div className="admin-info-banner info" role="status" style={{ marginTop: '0.5rem' }} data-testid="no-cost-notice">
                  {noCostTrips} of {detail.trips.length} trip{detail.trips.length === 1 ? '' : 's'} has no cost configured for
                  this provider, so the total is not known in advance. Enter the invoiced amount when you record the payment.
                </div>
              )}

              {/* Trips */}
              {tripError && <div className="admin-info-banner danger" role="alert" data-testid="trip-error" style={{ marginTop: '0.75rem' }}>{tripError}</div>}
              <div className="admin-table-wrapper" style={{ marginTop: '1rem' }}>
                <table className="admin-table" data-testid="trips-table">
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Leg</th>
                      <th>Completed</th>
                      <th>Driver</th>
                      <th style={{ textAlign: 'right' }}>Cost<InfoTip glossaryKey="providerCostPerTrip" /></th>
                      {isDraft && <th></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {detail.trips.length === 0 ? (
                      <tr>
                        <td colSpan={isDraft ? 6 : 5} style={{ textAlign: 'center', color: 'var(--admin-text-muted)' }} data-testid="trips-empty">
                          No trips on this settlement.
                        </td>
                      </tr>
                    ) : detail.trips.map((trip) => (
                      <tr key={trip.tripId} data-testid="trip-row">
                        <td style={{ fontWeight: 500 }}>{trip.orderNumber ?? `${trip.orderId.slice(0, 8)}…`}</td>
                        <td>{legLabel(trip.leg)}</td>
                        <td>{formatTripDate(trip.completedAt)}</td>
                        <td>{trip.driverName ?? '—'}</td>
                        <td style={{ textAlign: 'right' }}>
                          <Money amount={trip.costPerTrip} glossaryKey="providerCostPerTrip" hideTip />
                        </td>
                        {isDraft && (
                          <td style={{ textAlign: 'right' }}>
                            <button
                              type="button"
                              className="admin-btn admin-btn-sm admin-btn-outline"
                              disabled={busy}
                              onClick={() => excludeTrip(trip.tripId)}
                            >
                              Exclude
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Actions — DRAFT only */}
              {isDraft && (
                <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                  <button type="button" className="admin-btn admin-btn-sm admin-btn-primary" disabled={busy}
                    onClick={() => { setPanel(panel === 'pay' ? null : 'pay'); setPayError(null); }}>
                    Record payment
                  </button>
                  <button type="button" className="admin-btn admin-btn-sm admin-btn-outline" disabled={busy}
                    onClick={() => { setPanel(panel === 'void' ? null : 'void'); setVoidError(null); }}>
                    Void settlement
                  </button>
                </div>
              )}

              {isDraft && panel === 'void' && (
                <form className="admin-card" style={{ marginTop: '0.75rem' }} onSubmit={voidSettlement} data-testid="void-form">
                  <p style={{ fontSize: '0.8125rem', color: 'var(--admin-text-muted)', marginTop: 0 }}>
                    Voiding returns all {view.tripCount} trip{view.tripCount === 1 ? '' : 's'} to the pool; preparing
                    again collects the same trips.
                  </p>
                  <div className="form-group">
                    <label htmlFor="void-reason">Reason</label>
                    <input id="void-reason" type="text" className="form-input" value={voidReason}
                      onChange={(e) => setVoidReason(e.target.value)} />
                  </div>
                  {voidError && <div className="admin-info-banner danger" role="alert" data-testid="void-error">{voidError}</div>}
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="submit" className="admin-btn admin-btn-sm admin-btn-danger" disabled={busy}>
                      {busy ? 'Voiding…' : 'Confirm void'}
                    </button>
                    <button type="button" className="admin-btn admin-btn-sm admin-btn-outline" disabled={busy}
                      onClick={() => setPanel(null)}>Cancel</button>
                  </div>
                </form>
              )}

              {isDraft && panel === 'pay' && (
                <form className="admin-card" style={{ marginTop: '0.75rem' }} onSubmit={recordPayment} data-testid="pay-form">
                  <div className="form-group">
                    <label htmlFor="pay-reference">Payment reference (optional)</label>
                    <input id="pay-reference" type="text" className="form-input" value={reference}
                      onChange={(e) => setReference(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="pay-amount">Invoiced amount (AED)</label>
                    <input id="pay-amount" type="number" step="0.01" min="0" className="form-input" value={amount}
                      aria-required="true" aria-describedby="pay-amount-hint"
                      onChange={(e) => setAmount(e.target.value)} />
                    {/* #577 — the basis of the offered figure, stated. The old hint ("pre-filled
                        from the trip costs") was true and useless: for a VAT-registered contractor
                        the trip costs are ex-VAT and can never equal what they invoice, so an admin
                        who accepted the pre-fill under-paid them every time. */}
                    <div id="pay-amount-hint" style={{ fontSize: '0.6875rem', color: 'var(--admin-text-muted)' }} data-testid="pay-amount-hint">
                      {/* AC-43 / #576 — an unpriced contractor (price agreed at month end) is paid
                          once for the whole statement, so the figure is their invoiced total. */}
                      {view.amount == null
                        ? 'No cost per trip is set for this provider, so the amount must come from the invoice: '
                          + `enter their invoiced total for all ${view.tripCount} trip${view.tripCount === 1 ? '' : 's'} `
                          + 'on this statement, VAT included if they charge it.'
                        : vat != null
                          ? `Pre-filled with ${formatAed(view.amount + vat)}: ${formatAed(view.amount)} of trip costs `
                            + `plus ${formatAed(vat)} VAT — what this VAT-registered provider will invoice. `
                            + "Change it if their invoice differs."
                          : `Pre-filled with ${formatAed(view.amount)} of trip costs. This provider is not `
                            + "VAT-registered, so there is no VAT to add. Change it if their invoice differs."}
                    </div>
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 400 }}>
                      <input type="checkbox" checked={payVatRegistered} data-testid="pay-vat-registered"
                        onChange={(e) => setPayVatRegistered(e.target.checked)} />
                      Provider is VAT-registered
                    </label>
                    <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }} data-testid="pay-vat">
                      {payVatRegistered
                        ? `VAT claimed: ${Number(amount) > 0 ? formatAed(vatIncluded(Number(amount))) : '—'} (5/105 of the amount), in the ${monthLabel(dubaiToday())} VAT return`
                        : 'No VAT is claimed on this payment.'}
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="pay-proof">Provider invoice (optional — PDF, PNG, JPG)</label>
                    <input id="pay-proof" type="file" accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) => setProof(e.target.files?.[0] ?? null)} />
                  </div>
                  {payError && <div className="admin-info-banner danger" role="alert" data-testid="pay-error">{payError}</div>}
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="submit" className="admin-btn admin-btn-sm admin-btn-primary" disabled={busy}>
                      {busy ? 'Recording…' : 'Confirm payment'}
                    </button>
                    <button type="button" className="admin-btn admin-btn-sm admin-btn-outline" disabled={busy}
                      onClick={() => setPanel(null)}>Cancel</button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
